import { useMemo, useState } from "react";
import { Plus, Trophy, X, AlertCircle, IndianRupee } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { inrFull, inrPrecise, inrShort, formatAmountInput, parseAmountInput, moneyWords } from "@/lib/format";
import { computeLoan, compareLoans, toMonths, validateLoan, tenureSuggestion } from "@/lib/loanCompare";

const MIN_LOANS = 3;
const MAX_LOANS = 5;

const DEFAULT_LOANS = [
  { id: 1, name: "Bank A", principal: "1000000", rate: "9.99", years: "5", months: "0", extraMonthly: "0" },
  { id: 2, name: "Bank B", principal: "1000000", rate: "10.50", years: "5", months: "0", extraMonthly: "0" },
  { id: 3, name: "Bank C", principal: "1000000", rate: "9.75", years: "5", months: "0", extraMonthly: "0" },
];

const inputCls =
  "w-full rounded-xl border border-sand bg-white px-3 py-2.5 text-sm text-charcoal outline-none transition-colors placeholder:text-slate/50 focus:border-brass";
const labelCls = "mb-1 block text-[0.65rem] font-semibold uppercase tracking-wider text-slate";

// Subtle "amount in words" caption — muted, wraps, mobile-friendly.
const Words = ({ text, testid }) =>
  text ? (
    <p data-testid={testid} className="mt-1 text-[0.65rem] leading-snug text-slate/70">{text}</p>
  ) : null;

const METRIC_ROWS = [
  { key: "emi", label: "Monthly EMI", fmt: (c) => inrPrecise(c.emi), words: (c) => moneyWords(c.emi, "Rupees per month") },
  { key: "payoff", label: "Payoff time", fmt: (c, l) => formatTenure(c.payoffMonths, l._months, c.extraMonthly) },
  { key: "totalInterest", label: "Total interest payable", fmt: (c) => inrFull(c.totalInterest), words: (c) => moneyWords(c.totalInterest) },
  { key: "totalRepayment", label: "Total repayment (principal + interest)", fmt: (c) => inrFull(c.totalRepayment), words: (c) => moneyWords(c.totalRepayment) },
  { key: "interestPct", label: "Interest as % of principal", fmt: (c) => `${c.interestPct.toFixed(1)}% · ${inrFull(c.totalInterest)}`, words: (c) => moneyWords(c.totalInterest) },
];

function formatTenure(payoffMonths, scheduledMonths, extra) {
  if (payoffMonths == null) return "—";
  const fmt = (m) => {
    const y = Math.floor(m / 12);
    const mo = m % 12;
    if (y === 0) return `${mo}m`;
    if (mo === 0) return `${y}y`;
    return `${y}y ${mo}m`;
  };
  if (extra > 0 && payoffMonths < scheduledMonths) {
    return `${fmt(payoffMonths)} (was ${fmt(scheduledMonths)})`;
  }
  return fmt(scheduledMonths);
}

const formatMonths = (m) => formatTenure(m, m, 0);

// Chart tooltip with Indian formatting + amounts in words (kept out of the chart itself).
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="max-w-xs rounded-xl border border-sand bg-white p-3 text-xs shadow-[0_12px_30px_rgba(20,40,75,0.15)]">
      <p className="font-serif text-sm font-semibold text-concordia">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="mt-2">
          <p className="font-semibold" style={{ color: p.fill }}>{p.dataKey}: {inrFull(p.value)}</p>
          <p className="text-[0.65rem] leading-snug text-slate/70">{moneyWords(p.value)}</p>
        </div>
      ))}
    </div>
  );
};

export default function LoanComparePage() {
  useSEO({
    title: "Loan Comparison Calculator — Compare 3-5 Loan Offers | Loan Salaah",
    description: "Compare up to 5 loan offers side by side: EMI, total interest, total repayment, interest as % of principal, amortization schedules and an optional extra-payment (payout) option. Indian formatting, reducing-balance EMI.",
  });

  const [loans, setLoans] = useState(DEFAULT_LOANS);
  const [scheduleTab, setScheduleTab] = useState(0);

  const update = (id, key, value) =>
    setLoans((ls) => ls.map((l) => (l.id === id ? { ...l, [key]: value } : l)));

  const addLoan = () => {
    if (loans.length >= MAX_LOANS) return;
    const id = Math.max(...loans.map((l) => l.id)) + 1;
    setLoans((ls) => [...ls, { id, name: `Bank ${String.fromCharCode(64 + ls.length + 1)}`, principal: "1000000", rate: "9.99", years: "5", months: "0", extraMonthly: "0" }]);
  };

  const removeLoan = (id) => {
    if (loans.length <= MIN_LOANS) return;
    setLoans((ls) => ls.filter((l) => l.id !== id));
    setScheduleTab(0);
  };

  const analysis = useMemo(() => {
    const enriched = loans.map((l) => ({ ...l, _months: toMonths(l.years, l.months) }));
    const errors = enriched.map((l) => validateLoan({ ...l, months: l._months }));
    const computed = enriched.map((l, i) => (errors[i] ? null : computeLoan({ ...l, months: l._months })));
    const allValid = errors.every((e) => !e);
    const verdict = allValid ? compareLoans(computed, enriched) : null;
    const suggestions = enriched.map((l, i) =>
      errors[i] || computed[i]?.paymentTooLow ? null : tenureSuggestion({ ...l, months: l._months })
    );
    return { enriched, errors, computed, verdict, allValid, suggestions };
  }, [loans]);

  const { enriched, errors, computed, verdict, allValid, suggestions } = analysis;
  const chartData = allValid
    ? enriched.map((l, i) => ({
        name: l.name || `Loan ${i + 1}`,
        Principal: Math.round(Number(l.principal)),
        Interest: Math.round(computed[i].totalInterest),
      }))
    : [];
  const activeIdx = Math.min(scheduleTab, loans.length - 1);
  const activeComputed = computed[activeIdx];

  return (
    <div data-testid="compare-page" className="pb-24 pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Compare Loans</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl">
            Three to five offers. One clear winner.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate">
            Enter each lender's offer below — results update instantly. A lower EMI alone never means a cheaper
            loan: principal and tenure change the real cost. We show you the whole picture in ₹, using the
            reducing-balance EMI method every Indian lender uses.
          </p>
        </Reveal>

        {/* ---- loan input cards ---- */}
        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {enriched.map((loan, i) => (
            <Reveal key={loan.id} delay={i * 0.05}>
              <div data-testid={`loan-card-${i}`} className="rounded-3xl border border-sand bg-white p-6 shadow-[0_20px_50px_rgba(20,40,75,0.06)]">
                <div className="flex items-center justify-between gap-3">
                  <input
                    data-testid={`loan-name-input-${i}`}
                    className="w-full rounded-lg border border-transparent bg-transparent px-1 font-serif text-xl font-semibold text-concordia outline-none transition-colors focus:border-brass"
                    value={loan.name}
                    onChange={(e) => update(loan.id, "name", e.target.value)}
                    placeholder={`Loan ${i + 1}`}
                    aria-label="Loan or bank name"
                  />
                  <button
                    data-testid={`loan-remove-button-${i}`}
                    onClick={() => removeLoan(loan.id)}
                    disabled={loans.length <= MIN_LOANS}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-sand text-slate transition-colors hover:border-red-300 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label={`Remove ${loan.name}`}
                    title={loans.length <= MIN_LOANS ? "Minimum 3 loans" : "Remove this loan"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label htmlFor={`loan-principal-input-${i}`} className={labelCls}>Principal (₹)</label>
                    <input id={`loan-principal-input-${i}`} data-testid={`loan-principal-input-${i}`} type="text" inputMode="numeric" className={inputCls}
                      value={formatAmountInput(loan.principal)}
                      onChange={(e) => update(loan.id, "principal", parseAmountInput(e.target.value))} />
                    <Words testid={`loan-principal-words-${i}`}
                      text={loan.principal ? moneyWords(loan.principal) : "Amount in words will appear here."} />
                  </div>
                  <div>
                    <label htmlFor={`loan-rate-input-${i}`} className={labelCls}>Rate (% p.a.)</label>
                    <input id={`loan-rate-input-${i}`} data-testid={`loan-rate-input-${i}`} type="number" min="0" max="50" step="0.01" className={inputCls}
                      value={loan.rate} onChange={(e) => update(loan.id, "rate", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor={`loan-years-input-${i}`} className={labelCls}>Years</label>
                      <input id={`loan-years-input-${i}`} data-testid={`loan-years-input-${i}`} type="number" min="0" max="50" className={inputCls}
                        value={loan.years} onChange={(e) => update(loan.id, "years", e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor={`loan-months-input-${i}`} className={labelCls}>Months</label>
                      <input id={`loan-months-input-${i}`} data-testid={`loan-months-input-${i}`} type="number" min="0" max="11" className={inputCls}
                        value={loan.months} onChange={(e) => update(loan.id, "months", e.target.value)} />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label htmlFor={`loan-extra-input-${i}`} className={labelCls}>Extra monthly payout (₹, optional)</label>
                    <input id={`loan-extra-input-${i}`} data-testid={`loan-extra-input-${i}`} type="text" inputMode="numeric" className={inputCls}
                      value={formatAmountInput(loan.extraMonthly)}
                      onChange={(e) => update(loan.id, "extraMonthly", parseAmountInput(e.target.value))} />
                    <Words testid={`loan-extra-words-${i}`}
                      text={Number(loan.extraMonthly) > 0 ? moneyWords(loan.extraMonthly, "Rupees per month extra") : ""} />
                  </div>
                </div>
                {errors[i] && (
                  <p data-testid={`loan-error-${i}`} className="mt-3 flex items-center gap-1.5 text-xs text-red-700">
                    <AlertCircle className="h-3.5 w-3.5" /> {errors[i]}
                  </p>
                )}
                {!errors[i] && computed[i]?.paymentTooLow && (
                  <p data-testid={`loan-error-${i}`} className="mt-3 flex items-center gap-1.5 text-xs text-red-700">
                    <AlertCircle className="h-3.5 w-3.5" /> EMI + extra payment is too low to ever clear this balance.
                  </p>
                )}
              </div>
            </Reveal>
          ))}
          <button
            data-testid="compare-add-loan-button"
            onClick={addLoan}
            disabled={loans.length >= MAX_LOANS}
            className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-sand bg-white/50 text-slate transition-colors hover:border-brass hover:text-concordia disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-8 w-8" />
            <span className="text-sm font-semibold">Add Loan</span>
            <span className="text-xs">{loans.length}/{MAX_LOANS}</span>
          </button>
        </div>

        {/* ---- comparison table ---- */}
        {allValid && (
          <Reveal delay={0.1}>
            <h2 className="mt-16 font-serif text-3xl font-semibold tracking-tight text-concordia">Comparison summary</h2>
            <div className="mt-6 overflow-x-auto rounded-3xl border border-sand bg-white shadow-[0_20px_50px_rgba(20,40,75,0.06)]">
              <table className="w-full min-w-[640px] text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-sand text-left">
                    <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate">Metric</th>
                    {enriched.map((l, i) => (
                      <th key={l.id} className="px-5 py-4 text-left align-top">
                        <span className="font-serif text-base font-semibold text-concordia">{l.name || `Loan ${i + 1}`}</span>
                        {verdict && verdict.bestIdx === i && (
                          <span data-testid={`best-badge-${i}`} className="ml-2 inline-flex items-center gap-1 rounded-full bg-brass/15 px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-brass-deep">
                            <Trophy className="h-3 w-3" /> Lowest total cost
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRIC_ROWS.map((row) => (
                    <tr key={row.key} className="border-b border-sand/60 last:border-0">
                      <td className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate">{row.label}</td>
                      {computed.map((c, i) => (
                        <td key={enriched[i].id} data-testid={`compare-cell-${row.key}-${i}`}
                          className="px-5 py-3.5 align-top text-charcoal">
                          {row.fmt(c, enriched[i])}
                          {row.words && <Words testid={`compare-words-${row.key}-${i}`} text={row.words(c)} />}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-b border-sand/60 last:border-0 bg-champagne/40">
                    <td className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate">Smart tenure suggestion</td>
                    {computed.map((c, i) => {
                      const s = suggestions[i];
                      return (
                        <td key={enriched[i].id} data-testid={`compare-cell-tenureTip-${i}`} className="px-5 py-3.5 align-top font-medium text-concordia">
                          {s ? (
                            <span data-testid={`tenure-tip-${i}`}>
                              Cut tenure to <strong className="text-concordia">{formatMonths(s.months)}</strong> — save{" "}
                              <strong className="text-concordia">{inrFull(s.saves)}</strong> interest
                              <span className="block text-xs text-slate">
                                EMI rises {inrFull(s.emiDelta)}/mo (+{s.emiDeltaPct.toFixed(1)}%)
                              </span>
                              <Words text={moneyWords(s.saves)} />
                            </span>
                          ) : (
                            <span className="text-slate/60">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* verdict / savings note — winner always shown; ₹ savings only when comparable */}
            {verdict && (
              <div data-testid="savings-note" className="mt-6 rounded-2xl border border-brass/30 bg-brass/5 p-5">
                <p className="text-sm font-semibold text-concordia">
                  <Trophy className="mr-1.5 inline h-4 w-4 text-brass-deep" />
                  {enriched[verdict.bestIdx].name} has the lowest total cost — {inrFull(computed[verdict.bestIdx].totalRepayment)}.
                </p>
                <Words testid="savings-best-words" text={moneyWords(computed[verdict.bestIdx].totalRepayment)} />
                {verdict.comparable ? (
                  <ul className="mt-2 space-y-1 text-sm text-slate">
                    {computed.map((c, i) =>
                      i === verdict.bestIdx ? null : (
                        <li key={enriched[i].id} data-testid={`savings-line-${i}`}>
                          {enriched[i].name} costs {inrFull(verdict.savings[i])} more over the tenure.
                          <Words text={moneyWords(verdict.savings[i])} />
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <p data-testid="not-comparable-note" className="mt-2 text-sm leading-relaxed text-slate">
                    Note: these offers differ in principal or tenure, so rupee savings aren't apples-to-apples — a
                    lower EMI often just means a longer tenure (and more interest overall). Weigh{" "}
                    <em>total repayment</em> and <em>interest as % of principal</em> alongside.
                  </p>
                )}
              </div>
            )}

            {/* ---- chart ---- */}
            <h2 className="mt-14 font-serif text-3xl font-semibold tracking-tight text-concordia">Where your money goes</h2>
            <div className="mt-6 rounded-3xl border border-sand bg-white p-6 shadow-[0_20px_50px_rgba(20,40,75,0.06)]" data-testid="compare-chart">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9E2D2" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4D5E78" }} />
                  <YAxis tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 11, fill: "#4D5E78" }} width={70} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Bar dataKey="Principal" stackId="a" fill="#14284B" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Interest" stackId="a" fill="#B8913A" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* ---- amortization schedules ---- */}
            <h2 className="mt-14 font-serif text-3xl font-semibold tracking-tight text-concordia">Amortization schedule</h2>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="amortization-tabs">
              {enriched.map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`amortization-tab-${i}`}
                  onClick={() => setScheduleTab(i)}
                  className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors ${
                    i === activeIdx ? "bg-concordia text-ivory" : "border border-sand bg-white text-slate hover:border-concordia/40 hover:text-concordia"
                  }`}
                >
                  {l.name || `Loan ${i + 1}`}
                </button>
              ))}
            </div>
            {activeComputed && !activeComputed.paymentTooLow && (
              <div className="mt-4 max-h-96 overflow-auto rounded-3xl border border-sand bg-white shadow-[0_20px_50px_rgba(20,40,75,0.06)]">
                <table className="w-full min-w-[640px] text-sm" data-testid="amortization-table">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-sand text-left">
                      {["Period", "Principal paid", "Interest paid", "Outstanding balance"].map((h) => (
                        <th key={h} className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeComputed.schedule.map((row) => (
                      <tr key={row.period} className="border-b border-sand/60 last:border-0">
                        <td className="px-5 py-3 align-top font-medium text-charcoal">{row.period}</td>
                        <td className="px-5 py-3 align-top text-charcoal">
                          {inrFull(row.principalPaid)}
                          <Words text={moneyWords(row.principalPaid)} />
                        </td>
                        <td className="px-5 py-3 align-top text-charcoal">
                          {inrFull(row.interestPaid)}
                          <Words text={moneyWords(row.interestPaid)} />
                        </td>
                        <td className="px-5 py-3 align-top text-charcoal">
                          {inrFull(row.balance)}
                          <Words text={moneyWords(row.balance)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-slate/80">
              <IndianRupee className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brass" />
              Reducing-balance EMI, annual rates in % p.a., amounts in Indian numbering (₹1,00,000) with the amount
              in words beneath. Optional extra monthly payout shortens the tenure and cuts interest — shown in the
              payoff row. Figures are estimates; the lender's sanction letter is final.
            </p>
          </Reveal>
        )}
      </div>
    </div>
  );
}
